/**
 * Où se place la barre de playhead dans le piano roll, selon la place
 * disponible.
 *
 * Arithmétique pure, isolée du composant : elle est ainsi testable sans roll
 * ni renderer Pixi, et lisible côté React sans traîner le moteur de rendu.
 */

/**
 * Distance nominale (px CSS) entre la barre de playhead et le bas du roll,
 * sur un roll assez haut pour se l'offrir. Sert aussi de valeur d'amorçage à
 * l'observable `Studio.playheadDistance` (rien à mesurer avant le premier
 * layout Pixi).
 */
export const PLAYHEAD_DISTANCE_PX = 100;

/**
 * Hauteur de roll à partir de laquelle la distance nominale est tenue telle
 * quelle.
 *
 * Ces 100 px ne sont pas un décor : ils sont prélevés sur la fenêtre visible.
 * Le roll étale `visibleDuration` (4 s) sur toute sa hauteur, donc la playhead
 * dépense `distance / hauteur` de cette fenêtre à montrer le passé, et rend
 * d'autant moins d'anticipation. Sur un roll de ~800 px (poste de bureau) le
 * coût est de 12 %, ~3,5 s d'avance : c'est le réglage voulu, rien ne doit
 * bouger là.
 *
 * 520 est choisi au-dessus de ce que rend la cible du produit. Mesuré sur le
 * rendu réel : 591 px de roll à 1024×768 (le plus petit iPad en paysage),
 * 842 px à 1366×1024, 713 px à 1440×900, 859 px à 1920×1080. Tous tiennent la
 * distance nominale, au pixel près — le seuil ne change donc rien ni sur
 * l'iPad ni sur le poste de bureau.
 *
 * Les 71 px de marge du cas le plus serré (591 − 520) sont la réserve pour la
 * bande de touches : elle mesure 73 px à 1024×768 et va s'épaissir pour la
 * jouabilité au doigt. Elle peut doubler avant que l'iPad n'entre dans la
 * rampe ; au-delà de ~144 px de clavier, ce seuil est à réexaminer.
 */
const FULL_DISTANCE_ABOVE_PX = 520;

/**
 * Hauteur de roll au-dessous de laquelle la playhead redescend au niveau du
 * piano.
 *
 * Mesuré sur le rendu réel : 233 px de roll à 844×390, 218 px à 667×375,
 * 183 px à 852×340. Les mêmes 100 px y mangent la moitié de la fenêtre : il ne
 * reste que ~2 s d'anticipation, sur l'écran qui en a le plus besoin. La bande
 * de passé est ce qu'on sacrifie en premier — elle est agréable,
 * l'anticipation est ce qui permet de jouer.
 *
 * Conséquence assumée : à 0, il n'y a plus de bande de passé à l'écran, donc
 * plus de voile pour l'assombrir. `PastOverlay` se masque de lui-même sur une
 * hauteur nulle, il n'y a rien à couvrir entre la playhead et le bord bas.
 */
const ZERO_DISTANCE_BELOW_PX = 260;

/**
 * Part de la hauteur du roll qu'une réservation de pied ne peut pas dépasser.
 *
 * La distance est prélevée sur la fenêtre visible : à 100 % elle la mangerait
 * entière et `fromTick` passerait devant `toTick`. Ce plafond n'est pas là
 * pour arbitrer un goût mais pour qu'aucune réservation venue du DOM — une
 * vignette qu'on aurait laissée grandir, un écran qu'on n'a pas prévu — ne
 * puisse retourner le roll.
 *
 * 45 % laisse toujours la majorité de la fenêtre à l'anticipation, qui est ce
 * qui permet de lire la musique qui arrive.
 */
const MAX_FOOT_SHARE = 0.45;

/**
 * Distance playhead ↔ bas du roll pour une hauteur de roll donnée.
 *
 * Fonction de la **hauteur du roll**, jamais de la taille du viewport : la
 * bande de touches se place avant le roll et peut changer d'épaisseur sans que
 * la fenêtre bouge. Continue plutôt qu'à seuil sec : la hauteur ne change
 * qu'au redimensionnement, mais une rotation d'iPad traverse la bande et un
 * palier ferait sauter toutes les notes d'un coup.
 *
 * # La réservation de pied
 *
 * `footReserve` est la hauteur (px) que quelque chose occupe déjà au bas du
 * roll et que la playhead doit surplomber : aujourd'hui la caméra des mains
 * d'un replay, posée entre le clavier témoin et la ligne d'attaque. La note
 * franchit la ligne, tombe, atteint le clavier du rouleau, et les vraies mains
 * la jouent juste en dessous — une seule scène, lue de haut en bas.
 *
 * C'est un **maximum**, pas une somme : la bande de passé est déjà un espace
 * réservé, et la caméra vient l'habiter plutôt que s'y ajouter. Une réservation
 * plus courte que la distance nominale ne déplace donc rien.
 *
 * Seule autorité : la vue publie le résultat à chaque layout
 * (`Studio.playheadDistance`) et les overlays React le lisent via
 * `usePlayheadDistance()`, au lieu de recopier la constante.
 */
export function playheadDistanceFor(rollHeight: number, footReserve = 0): number {
  const reserve = Math.min(
    Math.max(0, Math.round(footReserve)),
    Math.floor(rollHeight * MAX_FOOT_SHARE),
  );

  return Math.max(nominalDistanceFor(rollHeight), reserve);
}

function nominalDistanceFor(rollHeight: number): number {
  if (rollHeight >= FULL_DISTANCE_ABOVE_PX) {
    return PLAYHEAD_DISTANCE_PX;
  }

  if (rollHeight <= ZERO_DISTANCE_BELOW_PX) {
    return 0;
  }

  const ramp =
    (rollHeight - ZERO_DISTANCE_BELOW_PX) /
    (FULL_DISTANCE_ABOVE_PX - ZERO_DISTANCE_BELOW_PX);

  return Math.round(PLAYHEAD_DISTANCE_PX * ramp);
}
